import { createContext, useContext, useState, useCallback, useMemo, useRef } from "react";
import { useBalances } from "@/hooks/wallet/useBalances";
import { useWalletConnection, isSocialLoginEnabled } from "@/hooks/wallet/useWalletConnection";
import { useChainManagement } from "@/hooks/wallet/useChainManagement";

export { isSocialLoginEnabled };

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const providerRef = useRef(null);
  const [chainId, setChainId] = useState(null);
  const { balance, usdtBalance, loadBalances, clearBalances } = useBalances(providerRef);
  const { address, isConnecting, connect, connectSocial, connectWalletConnect, disconnect } =
    useWalletConnection({ providerRef, setChainId, loadBalances, clearBalances });
  const { checkWalletRpc, addFSCNetwork, addUSDTToken } = useChainManagement({ providerRef, setChainId });

  const refreshBalances = useCallback((addr) => {
    const target = addr || address;
    if (target) loadBalances(target);
  }, [address, loadBalances]);

  const getProvider = useCallback(() => providerRef.current, []);

  const value = useMemo(
    () => ({
      address, isConnecting, connect, connectSocial, connectWalletConnect, disconnect, checkWalletRpc, addFSCNetwork, addUSDTToken,
      balance, usdtBalance, refreshBalances, chainId, getProvider,
    }),
    [address, isConnecting, connect, connectSocial, connectWalletConnect, disconnect, checkWalletRpc, addFSCNetwork, addUSDTToken,
      balance, usdtBalance, refreshBalances, chainId, getProvider],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet must be inside WalletProvider");
  return ctx;
}
